import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { get } from '../../lib/api.js';

export interface PublicTenant {
  slug: string;
  brandName: string;
  primaryColour: string | null;
  hasLogo: boolean;
}

export function usePublicTenant() {
  return useQuery({
    queryKey: ['public-tenant'],
    queryFn: () => get<PublicTenant>('/public/tenant'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Every signed-out screen (D-123): the organisation's brand on the website's black on one side, the
 * form on the other; on a phone the brand is a band above the form.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  const tenant = usePublicTenant();
  const name = tenant.data?.brandName ?? 'Tatra CRM';
  return (
    <main className="flex min-h-dvh flex-col bg-bg lg:flex-row">
      <section
        aria-label={name}
        className="relative flex shrink-0 flex-col justify-between overflow-hidden bg-nav-bg px-6 py-6 text-nav-text lg:w-[44%] lg:px-12 lg:py-12"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -bottom-48 h-[28rem] w-[28rem] rounded-full bg-[radial-gradient(closest-side,rgb(236_31_156/0.35),rgb(168_85_247/0.12)_60%,transparent)] lg:-right-24 lg:-bottom-24 lg:h-[36rem] lg:w-[36rem]"
        />
        <div className="relative flex items-center gap-3">
          {tenant.data?.hasLogo ? (
            <img src="/api/v1/public/tenant/logo" alt="" className="h-9 max-w-[160px] object-contain" />
          ) : (
            <span
              aria-hidden="true"
              className="h-3 w-3 rounded-full bg-[linear-gradient(135deg,#ff6fc9,#ec1f9c_45%,#a855f7)] shadow-[0_0_0_4px_rgb(236_31_156/0.2)]"
            />
          )}
          <span className="font-display text-lg font-semibold tracking-[-0.01em]">{name}</span>
        </div>
        <div className="relative hidden max-w-md lg:block">
          <p className="font-display text-[2.5rem] leading-[1.08] font-semibold tracking-[-0.03em]">
            Every enquiry, visit and booking, <span className="text-nav-active">in one place.</span>
          </p>
          <p className="mt-4 text-nav-muted">
            Leads, projects, site plans, bookings and partners for {name}.
          </p>
        </div>
      </section>
      <div className="flex flex-1 items-start justify-center px-4 py-8 sm:items-center sm:py-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-2xl font-semibold tracking-[-0.02em] text-text">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-text-muted">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </main>
  );
}
